<template>
  <div class="sm:col-span-2 2xl:col-span-3">
    <div
      class="relative flex flex-col h-full bg-white rounded shadow-lg t3m-ObjectiveFinder-details t3m-ObjectiveFinder-listItem font-condensed isolate md:rounded-lg ring-1 ring-gray-900"
      :data-pointer-x-percentage="pointAtColumn"
    >
      <span
        class="absolute inline-block w-0 h-0 border-t-0 border-b-gray-900 border-t-transparent border-x-transparent border-b-6 border-x-6 -mt-6px"
        :class="{
          'left-[50%]': pointAtColumn === '1-of-1' || pointAtColumn === '2-of-3',
          'left-[25%]': pointAtColumn === '1-of-2',
          'left-[75%]': pointAtColumn === '2-of-2',
          'left-[16%]': pointAtColumn === '1-of-3',
          'left-[84%]': pointAtColumn === '3-of-3',
        }"
      ></span>
      <b-tabs
        v-model="tabIndex"
        content-class="t3m-ObjectiveFinder-listItem-body"
        nav-wrapper-class="px-2 sm:px-4 pt-2 text-white bg-gray-900 rounded-t md:rounded-t-lg t3m-Navigation t3m-Navigation--objectiveFinderDetails"
        nav-class="flex flex-wrap mb-0 md:space-x-1 t3m-Tabs-nav"
      >
        <template v-slot:tabs-start>
          <li
            role="presentation"
            class="self-center w-full truncate nav-item lg:w-auto"
          >
            <h4
              class="mb-4 text-sm truncate lg:mb-0 md:text-base t3m-ObjectiveFinder-listItem-title"
            >
              {{ selectedObjective.objectiveName }}
            </h4>
          </li>
        </template>

        <b-tab
          :title="translations.TechnicalSpecs"
          active
          title-link-class="px-2 sm:px-4 py-2 text-xs text-white truncate bg-opacity-50 rounded-t hover:bg-gray-600 hover:text-white md:text-base"
          title-item-class="lg:!ml-auto flex"
        >
          <div class="sm:grid sm:grid-cols-12">
            <div class="sm:col-span-7 xl:col-span-8">
              <div class="grid grid-cols-2 2xl:grid-cols-3 md:gap-4 md:p-6">
                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ selectedObjective.aperture }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.NumericalAperture }}
                  </div>
                </div>

                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ canDoLedafValue(selectedObjective) }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.SuitableForAFC }}
                  </div>
                </div>

                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ corrValue(selectedObjective) }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.CorrectionRing }}
                  </div>
                </div>

                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ coverGlassValue(selectedObjective) }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.Coverglass }}
                  </div>
                </div>

                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ exitPupilPosValue(selectedObjective) }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.ExitPupilPositionDICprism }}
                  </div>
                </div>

                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ selectedObjective.fov }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.FieldOfView }}
                  </div>
                </div>

                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ immersion(selectedObjective) }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.Immersion }}
                  </div>
                </div>

                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ irisDiaphragmValue(selectedObjective) }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.IrisDiaphragm }}
                  </div>
                </div>

                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ magnificationValue(selectedObjective) }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.Magnification }}
                  </div>
                </div>

                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ selectedObjective.freeWorkingDistance }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.FreeWorkingDistance }}
                  </div>
                </div>

                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ selectedObjective.objectiveType }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.ObjectiveType }}
                  </div>
                </div>

                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ selectedObjective.matnrShort }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.ArticleNumber }}
                  </div>
                </div>
                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ selectedObjective.objectiveThread }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.ObjectiveThread }}
                  </div>
                </div>
                <div
                  class="flex flex-col p-2 border-b t3m-ObjectiveFinder-details-spec border-b-t3m-body md:border-b-0"
                >
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ springLoaded(selectedObjective) }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.SpringLoaded }}
                  </div>
                </div>
                <div class="flex flex-col p-2 t3m-ObjectiveFinder-details-spec">
                  <div
                    class="text-xl font-bold t3m-ObjectiveFinder-details-spec-value"
                  >
                    {{ transmission(selectedObjective) }}
                  </div>
                  <div
                    class="mt-1 text-sm border-t t3m-ObjectiveFinder-details-spec-title text-t3m-muted md:pt-1 md:border-t3m-border-color"
                  >
                    {{ translations.Transmission }}
                  </div>
                </div>
              </div>
            </div>
            <div class="sm:col-span-5 xl:col-span-4 t3m-bg-gray-100">
              <div
                class="flex flex-col h-full justify-start t3m-ObjectiveFinder-details-image"
              >
                <img
                  :src="selectedObjective.picturePath"
                  class="w-full mx-auto mt-4 max-w-56 saturate-20 mix-blend-multiply"
                />
                <div class="px-4 pt-4 pb-8">
                  <div class="hidden">
                    <div>shopEnabled: {{ shopEnabled }}</div>
                    <div>showShopPanel: {{ showShopPanel }}</div>
                    <div>isShopProduct: {{ isShopProduct }}</div>
                  </div>
                  <ShopPanel
                    v-if="shouldShowShopPanel"
                    :objective="selectedObjective"
                  />
                  <div v-else class="flex items-center justify-center">
                    <a
                      data-fancybox
                      data-width="1024"
                      data-type="iframe"
                      rel="nofollow"
                      :href="encodedContactURL()"
                      class="t3m-ObjectiveFinder-list-action-contact t3m-FancyboxTrigger t3m-FancyboxTrigger--contact btn btn-sm btn-primary"
                      >{{ translations.GetQuote }}</a
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-tab>

        <b-tab
          :title="translations.TransmissionCurve"
          v-if="selectedObjective.transmissionCurveDataPath"
          title-link-class="px-2 sm:px-4 py-2 text-xs text-white bg-opacity-50 rounded-t hover:bg-gray-600 hover:text-white md:text-base"
          title-item-class="flex"
        >
          <div
            class="t3m-ObjectiveFinder-details-transmissionCurve md:p-6 md:min-h-96"
          >
            <div
              class="t3m-ObjectiveFinder-details-transmissionCurveImage"
              v-if="this.$root.$browserDetect.isIE"
            >
              <img
                :src="selectedObjective.transmissionCurvePicturePath"
                loading="lazy"
              />
            </div>
            <chart
              v-if="!this.$root.$browserDetect.isIE"
              :objectives="[selectedObjective]"
            />
            <small class="text-muted"
              >Transmission data is calculated according to ISO 19012-3.
              Measured values can vary due to manufacturing tolerances.</small
            >
          </div>
        </b-tab>

        <b-tab
          :title="translations.DimensionSheet"
          v-if="selectedObjective.dimensionSheetPath"
          title-link-class="px-2 sm:px-4 py-2 text-xs text-white bg-opacity-50 rounded-t hover:bg-gray-600 hover:text-white md:text-base"
          title-item-class="flex"
        >
          <div
            class="flex items-center justify-center cursor-pointer t3m-ObjectiveFinder-details-dimensionSheet md:p-6"
          >
            <img
              :src="selectedObjective.dimensionSheetPath"
              class="max-w-full w-96"
              loading="lazy"
              @click="lightboxIndex = 0"
            />
          </div>
        </b-tab>
      </b-tabs>

      <footer
        class="items-center justify-between block px-4 py-3 text-white bg-gray-900 rounded-b md:rounded-b-lg md:flex t3m-ObjectiveFinder-listItem-footer"
      >
        <div
          class="flex flex-wrap items-center mb-4 space-x-2 t3m-ObjectiveFinder-details-actions md:mb-0 md:order-2 md:ml-auto"
        >
          <button class="btn btn-sm btn-outline-light" @click="hideDetails">
            {{ translations.Close }}
          </button>
          <button class="hidden btn btn-sm btn-light">
            <i class="fa fa-file-pdf-o"></i>
            Download Datasheet
          </button>
        </div>

        <div class="flex items-center space-x-4 md:order-1 md:mr-4">
          <a
            :href="selectedObjective.singleViewPageUrl"
            target="_blank"
            class="flex items-center space-x-2 text-white hover:text-white t3m-ObjectiveFinder-details-permaLink opacity-60 hover:opacity-100"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              class="w-3 h-3"
              fill="currentColor"
            >
              <path
                d="M314.222 197.78c51.091 51.091 54.377 132.287 9.75 187.16-6.242 7.73-2.784 3.865-84.94 86.02-54.696 54.696-143.266 54.745-197.99 0-54.711-54.69-54.734-143.255 0-197.99 32.773-32.773 51.835-51.899 63.409-63.457 7.463-7.452 20.331-2.354 20.486 8.192a173.31 173.31 0 0 0 4.746 37.828c.966 4.029-.272 8.269-3.202 11.198L80.632 312.57c-32.755 32.775-32.887 85.892 0 118.8 32.775 32.755 85.892 32.887 118.8 0l75.19-75.2c32.718-32.725 32.777-86.013 0-118.79a83.722 83.722 0 0 0-22.814-16.229c-4.623-2.233-7.182-7.25-6.561-12.346 1.356-11.122 6.296-21.885 14.815-30.405l4.375-4.375c3.625-3.626 9.177-4.594 13.76-2.294 12.999 6.524 25.187 15.211 36.025 26.049zM470.958 41.04c-54.724-54.745-143.294-54.696-197.99 0-82.156 82.156-78.698 78.29-84.94 86.02-44.627 54.873-41.341 136.069 9.75 187.16 10.838 10.838 23.026 19.525 36.025 26.049 4.582 2.3 10.134 1.331 13.76-2.294l4.375-4.375c8.52-8.519 13.459-19.283 14.815-30.405.621-5.096-1.938-10.113-6.561-12.346a83.706 83.706 0 0 1-22.814-16.229c-32.777-32.777-32.718-86.065 0-118.79l75.19-75.2c32.908-32.887 86.025-32.755 118.8 0 32.887 32.908 32.755 86.025 0 118.8l-45.848 45.84c-2.93 2.929-4.168 7.169-3.202 11.198a173.31 173.31 0 0 1 4.746 37.828c.155 10.546 13.023 15.644 20.486 8.192 11.574-11.558 30.636-30.684 63.409-63.457 54.733-54.735 54.71-143.3-.001-197.991z"
              />
            </svg>
            <span>{{ translations.DetailsPage }}</span>
          </a>

          <span
            class="flex items-center space-x-2 text-white cursor-pointer hover:text-white t3m-ObjectiveFinder-details-clipboard opacity-60 hover:opacity-100"
            :class="{ 'text-t3m-malachite-500 !opacity-100': copied }"
            v-clipboard="selectedObjective.singleViewPageUrl"
            v-clipboard:success="clipboardSuccessHandler"
            v-clipboard:error="clipboardErrorHandler"
          >
            <svg
              v-if="!copied"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 384 512"
              class="w-3 h-3"
              fill="currentColor"
            >
              <path
                d="M336 64h-80c0-35.3-28.7-64-64-64s-64 28.7-64 64H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM192 40c13.3 0 24 10.7 24 24s-10.7 24-24 24-24-10.7-24-24 10.7-24 24-24zm144 418c0 3.3-2.7 6-6 6H54c-3.3 0-6-2.7-6-6V118c0-3.3 2.7-6 6-6h42v36c0 6.6 5.4 12 12 12h168c6.6 0 12-5.4 12-12v-36h42c3.3 0 6 2.7 6 6z"
              />
            </svg>
            <svg
              v-if="copied"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 384 512"
              class="w-3 h-3"
              fill="currentColor"
            >
              <path
                d="M269.3 225.8c-3.9-3.9-10.2-3.9-14.1-.1l-88 87.3-38.1-38.5c-3.9-3.9-10.2-3.9-14.1-.1l-23.6 23.4c-3.9 3.9-3.9 10.2-.1 14.1l68.5 69.1c3.9 3.9 10.2 3.9 14.1.1l118.6-117.6c3.9-3.9 3.9-10.2.1-14.1l-23.3-23.6zM336 64h-80c0-35.3-28.7-64-64-64s-64 28.7-64 64H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM192 48c8.8 0 16 7.2 16 16s-7.2 16-16 16-16-7.2-16-16 7.2-16 16-16zm144 408c0 4.4-3.6 8-8 8H56c-4.4 0-8-3.6-8-8V120c0-4.4 3.6-8 8-8h40v32c0 8.8 7.2 16 16 16h160c8.8 0 16-7.2 16-16v-32h40c4.4 0 8 3.6 8 8v336z"
              />
            </svg>
            <span>{{ translations.Copy }}</span>
          </span>
        </div>
      </footer>
    </div>
    <CoolLightBox
      :items="[selectedObjective.dimensionSheetPath]"
      :index="lightboxIndex"
      @close="lightboxIndex = null"
    >
    </CoolLightBox>
  </div>
</template>

<script>
import { valueFormatters } from '../shared'
import { mapState, mapActions } from 'vuex'
import { BTabs, BTab } from 'bootstrap-vue'
import Chart from '@/components/Chart.vue'
import CoolLightBox from 'vue-cool-lightbox'
import ShopPanel from '@/components/ShopPanel.vue'

export default {
  name: 'ObjectiveDetails',
  data() {
    return {
      pointAtColumn: '',
      copied: false,
      lightboxIndex: null,
      showShopPanel: true,
    }
  },
  components: {
    BTabs,
    BTab,
    Chart,
    CoolLightBox,
    ShopPanel,
  },
  mounted: function() {
    this.setElementPosition()
  },
  beforeDestroy() {
    // Cleanup any listeners or references here
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
  },
  computed: {
    ...mapState([
      'contactURL',
      'selectedObjective',
      'selectedObjectiveComponent',
      'selectedDetailTabIndex',
      'objectiveListWidth',
      'objectiveColumnWidth',
      'translations',
      'shopEnabled',
    ]),

    tabIndex: {
      get() {
        return this.selectedDetailTabIndex
      },
      set(indx) {
        this.setSelectedDetailTabIndex(indx)
      },
    },
    isShopProduct() {
      return Boolean(
        (this.selectedObjective &&
          this.selectedObjective.availableonline &&
          this.selectedObjective.availableonline.length) ||
          (this.selectedObjective &&
            this.selectedObjective.availabletopurchase &&
            this.selectedObjective.availabletopurchase.length)
      )
    },
    shouldShowShopPanel() {
      return this.shopEnabled && this.showShopPanel && this.isShopProduct
    },
  },
  watch: {
    selectedObjective: function() {
      this.setElementPosition()
      // showShopPanel is used to force reinitialization of the shop panel by DMF
      this.showShopPanel = false
      this.$nextTick(() => {
        this.showShopPanel = true
      })
    },
  },
  mixins: [valueFormatters],
  methods: {
    ...mapActions([
      'selectObjective',
      'setObjectiveColumnWidth',
      'setSelectedDetailTabIndex',
    ]),
    hideDetails() {
      // Just notify parent - let Vue handle the cleanup
      this.selectObjective({
        objectiveComponent: undefined,
        targetTabIndex: 0,
      })
    },

    setElementPosition() {
      let listElement = this.$parent.$el
      let selectedObjectiveElement = this.selectedObjectiveComponent.$el

      if (!listElement || !selectedObjectiveElement) {
        return
      }

      // Remove previous instance if exists
      if (this.$el.parentNode) {
        this.$el.parentNode.removeChild(this.$el)
      }

      let listRect = listElement.getBoundingClientRect()
      let objectiveRect = selectedObjectiveElement.getBoundingClientRect()

      let cols = Math.floor(listRect.width / objectiveRect.width)
      let colPos =
        Math.round((objectiveRect.left - listRect.left) / objectiveRect.width) +
        1
      let colOffset = cols - colPos + 1

      let colIndex = Array.prototype.indexOf.call(
        listElement.children,
        selectedObjectiveElement
      )

      listElement.insertBefore(
        this.$el,
        listElement.children[colIndex + colOffset]
      )

      this.pointAtColumn = colPos + '-of-' + cols
    },
    clipboardSuccessHandler() {
      this.copied = true
    },
    clipboardErrorHandler() {
      this.copied = false
    },
  },
}
</script>
